import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { CreateGiftpassBatchInput, ImportPrintedGiftpassesInput } from "@bednbody/types";
import { callEdgeFunction } from "@/lib/edge-functions";

export interface GiftPassBatchSummary {
  id: string;
  /** giftpass_batches has no `name` column — company/campaign is the label (0008). */
  name: string;
  status: string;
  createdAt: string | null;
  totalPasses: number;
  active: number;
  /** Used on a booking (redeemed). */
  used: number;
  /** Where the batch came from: an order, an import of printed cards, or Generate. */
  kind: "order" | "printed" | "generated";
}

export function useGiftPassBatches() {
  return useQuery({
    queryKey: ["giftpass-batches"],
    queryFn: async (): Promise<GiftPassBatchSummary[]> => {
      const { data, error } = await supabase
        .from("giftpass_batches")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;

      // ⚠ COUNTED, NOT FETCHED. This used to load every pass row and count in
      // the browser, and PostgREST returns at most 1000 rows — so once more
      // than 1000 passes existed (one generated batch can hold 1000, 0223) the
      // totals silently stopped at the cap. Two head-only counts per batch
      // instead; batches are few.
      const countPasses = async (batchId: string, only?: "active" | "redeemed") => {
        let query = supabase
          .from("giftpasses")
          .select("id", { count: "exact", head: true })
          .eq("batch_id", batchId);
        if (only) query = query.eq("status", only);
        const { count, error: countError } = await query;
        if (countError) throw countError;
        return count ?? 0;
      };

      return Promise.all(
        (data ?? []).map(async (b) => {
          const [totalPasses, active, used] = await Promise.all([
            countPasses(b.id),
            countPasses(b.id, "active"),
            countPasses(b.id, "redeemed"),
          ]);
          const name = [b.company, b.campaign].filter(Boolean).join(" · ") || "Unnamed batch";
          return {
            id: b.id,
            name,
            status: b.status ?? "active",
            createdAt: b.created_at,
            totalPasses,
            active,
            used,
            kind: b.order_id ? "order" : name.startsWith("Printed cards") ? "printed" : "generated",
          };
        }),
      );
    },
    staleTime: 2 * 60_000,
  });
}

export function useGiftPasses(batchId: string | null) {
  return useQuery({
    queryKey: ["giftpass-passes", batchId],
    queryFn: async () => {
      if (!batchId) return [];

      // The booking each card paid for, by its ref (Cards tab, 2026-10-30).
      const { data, error } = await supabase
        .from("giftpasses")
        .select("*, booking:bookings!giftpasses_redeemed_booking_id_fkey(ref)")
        .eq("batch_id", batchId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!batchId,
    staleTime: 1 * 60_000,
  });
}

export function useInvalidateBatch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (batchId: string) => {
      // giftpass_batches (0008) directly grants admin/superadmin INSERT+UPDATE
      // — unlike giftpasses/mileage tables, this one is meant to be a plain
      // RLS-guarded write, not an Edge Function (API Conventions: simple
      // reads/writes with no cross-table transaction stay on the client).
      const { error } = await supabase
        .from("giftpass_batches")
        .update({ status: "inactive" })
        .eq("id", batchId);

      if (error) throw error;
      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["giftpass-batches"] });
    },
  });
}

export function useReactivatePass() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (passId: string) => {
      // `giftpasses` only grants admin/superadmin SELECT (0008) — reactivation
      // is scoped server-side to 'revalidation_required' passes only, so a
      // pass already consumed against a booking can never be double-spent
      // (reactivate_giftpass, migration 0022).
      await callEdgeFunction(`admin/giftpasses/${passId}/reactivate`, {
        data: { reason: "Admin one-click reactivation from GiftPass Management." },
      });
      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["giftpass-passes"] });
    },
  });
}

/**
 * Generate a batch and its passes (POST admin/giftpass-batches, 0223). The
 * passes are created server-side — `giftpasses` grants this app SELECT only.
 */
export function useCreateGiftPassBatch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateGiftpassBatchInput) =>
      callEdgeFunction<{ batchId: string; count: number }>("admin/giftpass-batches", {
        data: input,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["giftpass-batches"] });
      queryClient.invalidateQueries({ queryKey: ["giftpass-passes"] });
    },
  });
}

/** Printed cards by number (POST admin/giftpass-imports, 0376). Superadmin only. */
export function useImportPrintedGiftpasses() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: ImportPrintedGiftpassesInput) =>
      callEdgeFunction<{ batchId: string; count: number }>("admin/giftpass-imports", {
        data: input,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["giftpass-batches"] });
      queryClient.invalidateQueries({ queryKey: ["giftpass-passes"] });
    },
  });
}
